import { createContext, type ReactNode, useContext, useMemo } from 'react';

import type { UseObservabilityFunctions } from '@opendoor/observability';

// Align with the `useObservability()` hook return — what every host actually
// injects via AtomsProvider — not the full `Observability` object. The hook's
// `trackJourneyEvent` omits the `contextualData` parameter, so typing against
// `Observability` here would reject every real host hook.
export type TrackJourneyEvent = UseObservabilityFunctions['trackJourneyEvent'];

const noopTrackJourneyEvent: TrackJourneyEvent = () => {};

export interface AtomsObservability {
  trackJourneyEvent: TrackJourneyEvent;
}

/**
 * Injection seam mirroring bricks-next's `IAnalyticsContext`. The host provides
 * a `useObservability` hook (called during render) returning an object with a
 * `trackJourneyEvent` method.
 */
export interface AtomsAnalyticsContextValue {
  useObservability: () => AtomsObservability;
}

/**
 * No-op default so an un-wrapped tree never crashes — the fail-safe property.
 * Omitting the provider (or its `useObservability`) silently disables telemetry.
 */
export const defaultAnalyticsContext: AtomsAnalyticsContextValue = {
  useObservability: () => ({
    trackJourneyEvent: noopTrackJourneyEvent,
  }),
};

const AnalyticsContext = createContext<AtomsAnalyticsContextValue>(defaultAnalyticsContext);

/** Read the analytics seam. Intended for `useObservabilitySafe`, not components directly. */
export function useAnalyticsContext(): AtomsAnalyticsContextValue {
  return useContext(AnalyticsContext);
}

export interface AtomsProviderProps {
  /**
   * Hook returning `{ trackJourneyEvent }`. Called during render, so it may use
   * hooks. Defaults to a no-op when omitted.
   */
  useObservability?: AtomsAnalyticsContextValue['useObservability'];
  children: ReactNode;
}

/**
 * Wrap an app to inject an observability implementation into atoms components.
 *
 * @example
 * ```tsx
 * import { AtomsProvider } from '@opendoor/atoms/utils';
 * import { globalObservability } from '@opendoor/observability';
 *
 * const useObservability = () => ({
 *   trackJourneyEvent: globalObservability.trackJourneyEvent,
 * });
 *
 * <AtomsProvider useObservability={useObservability}>{app}</AtomsProvider>
 * ```
 */
export function AtomsProvider({ useObservability, children }: AtomsProviderProps) {
  const value = useMemo<AtomsAnalyticsContextValue>(
    () => ({ useObservability: useObservability ?? defaultAnalyticsContext.useObservability }),
    [useObservability],
  );
  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}
